import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MarkerInfo } from '../map-with-markers/map-with-markers';

// Zoom level constants for better readability
const ZOOM_LEVELS = {
  COUNTRY: 6,
  REGION: 8,
  CITY: 10,
  NEIGHBORHOOD: 12,
  STREET: 15,
  BUILDING: 18
} as const;

// Distance thresholds in kilometers
const DISTANCE_THRESHOLDS = {
  VERY_FAR: 1000,
  FAR: 500,
  MODERATE: 100,
  CLOSE: 50
} as const;

// Helper function to calculate the distance between two coordinates (Haversine formula)
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Radius of the Earth in kilometers
  const toRadians = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Get zoom level based on distance between markers
const getZoomFromDistance = (distance: number): number => {
  if (distance > DISTANCE_THRESHOLDS.VERY_FAR) return 4;
  if (distance > DISTANCE_THRESHOLDS.FAR) return ZOOM_LEVELS.COUNTRY;
  if (distance > DISTANCE_THRESHOLDS.MODERATE) return ZOOM_LEVELS.REGION;
  if (distance > DISTANCE_THRESHOLDS.CLOSE) return ZOOM_LEVELS.CITY;
  return ZOOM_LEVELS.NEIGHBORHOOD;
};

const useMapBounds = (markers: MarkerInfo[]) => {
  // Start with null to indicate "not calculated yet"
  const [centerPosition, setCenterPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [bounds, setBounds] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);

  // Track previous markers to prevent infinite loops
  const prevMarkersRef = useRef<string>('');

  // Serialize markers for comparison
  const markersKey = JSON.stringify(
    markers.map((m) => ({
      id: m.id,
      lat: m.lat,
      lng: m.lng,
      type: m.type
    }))
  );

  // New approach: Calculate padded bounds for fitBounds
  const calculatePaddedBounds = useCallback(() => {
    let newBounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    } | null = null;

    if (markers.length === 0) {
      newBounds = null;
    } else if (markers.length === 1) {
      const { lat, lng, type } = markers[0];
      // For a destination marker we return null bounds so MapWithMarkers can
      // use the Places API viewport instead; a city-level zoom is used as fallback.
      if (type === 'destination') {
        newBounds = null;
      } else {
        const padding = 0.01; // Small padding for single non-destination marker
        newBounds = {
          north: lat + padding,
          south: lat - padding,
          east: lng + padding,
          west: lng - padding
        };
      }
    } else {
      // For multiple markers, calculate bounds with 10% padding
      const lats = markers.map((m) => m.lat);
      const lngs = markers.map((m) => m.lng);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Add 10% padding to the bounds
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;

      newBounds = {
        north: maxLat + latDiff * 0.1,
        south: minLat - latDiff * 0.1,
        east: maxLng + lngDiff * 0.1,
        west: minLng - lngDiff * 0.1
      };
    }

    // Only update if values changed
    setBounds((prev) => {
      if (!newBounds && !prev) return prev;
      if (!newBounds || !prev) return newBounds;
      if (
        prev.north === newBounds.north &&
        prev.south === newBounds.south &&
        prev.east === newBounds.east &&
        prev.west === newBounds.west
      ) {
        return prev;
      }
      return newBounds;
    });
  }, [markers]);

  const calculateCenterPosition = useCallback(() => {
    let newCenter: { lat: number; lng: number };

    if (markers.length === 0) {
      newCenter = { lat: 0, lng: 0 };
    } else if (markers.length === 1) {
      // For a single marker, use its exact position
      const { lat, lng } = markers[0];
      newCenter = { lat, lng };
    } else {
      // For multiple markers, calculate the center of the bounding box
      const lats = markers.map((m) => m.lat);
      const lngs = markers.map((m) => m.lng);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Center is the midpoint of the bounding box
      const lat = (minLat + maxLat) / 2;
      const lng = (minLng + maxLng) / 2;
      newCenter = { lat, lng };
    }

    // Only update if values changed
    setCenterPosition((prev) => {
      if (prev && prev.lat === newCenter.lat && prev.lng === newCenter.lng) {
        return prev;
      }
      return newCenter;
    });
  }, [markers]);

  const calculateZoomLevel = useCallback(() => {
    const markerCount = markers.length;
    let newZoom: number;

    // Handle different marker scenarios
    if (markerCount === 0) {
      newZoom = ZOOM_LEVELS.COUNTRY;
    } else if (markerCount === 1) {
      // For single destination, use city-level zoom instead of street-level
      const singleMarker = markers[0];
      const isDestination = singleMarker.type === 'destination';
      newZoom = isDestination ? ZOOM_LEVELS.COUNTRY : ZOOM_LEVELS.CITY;
    } else {
      // Calculate bounds for multiple markers
      const lats = markers.map((m) => m.lat);
      const lngs = markers.map((m) => m.lng);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Add 10% padding to the bounds
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;

      const paddedMinLat = minLat - latDiff * 0.1;
      const paddedMaxLat = maxLat + latDiff * 0.1;
      const paddedMinLng = minLng - lngDiff * 0.1;
      const paddedMaxLng = maxLng + lngDiff * 0.1;

      const distance = calculateDistance(
        paddedMinLat,
        paddedMinLng,
        paddedMaxLat,
        paddedMaxLng
      );
      newZoom = getZoomFromDistance(distance);
    }

    // Only update if value changed
    setZoom((prev) => (prev === newZoom ? prev : newZoom));
  }, [markers]);

  // Calculate center and zoom when markers change
  useEffect(() => {
    // Only recalculate if markers actually changed
    if (prevMarkersRef.current === markersKey) {
      console.log('[useMapBounds] Markers unchanged, skipping calculation');
      return;
    }

    console.log(
      `[useMapBounds] Calculating bounds for ${markers.length} markers`
    );
    prevMarkersRef.current = markersKey;
    calculateCenterPosition();
    calculateZoomLevel();
    calculatePaddedBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markersKey]);

  // Return safe defaults if not calculated yet
  // Use refs to ensure stable object references when values haven't changed
  const returnValue = useMemo(
    () => ({
      centerPosition: centerPosition || { lat: 0, lng: 0 },
      zoom: zoom || ZOOM_LEVELS.CITY,
      bounds: bounds // New: return bounds for fitBounds approach
    }),
    [centerPosition, zoom, bounds]
  );

  return returnValue;
};

export default useMapBounds;
